/**
 * Reusable utility for fetching UI content from the MGM Content API (CMS) by ID.
 */

import { useCallback, useEffect, useState } from 'react';

import { post } from '../../api/http';
import { captureException } from '../../monitoring/logrocket';
import { ensureTransientToken } from '../../utils/auth/ensureTransientToken';

/**
 * GQL query string sent to the Content API. Exposed for parity with the
 * reference implementation and for tests / non-hook callers that want the
 * raw query text.
 */
export const GET_UI_CONTENT_BY_ID = `
  query GetUIContentById($getUiContentByIdId: String) {
    getUIContentById(id: $getUiContentByIdId)
  }
`;

/**
 * Centralized registry of all CMS UI content IDs used across the Identity Widget.
 * Add new content IDs here so they are discoverable and reusable by any feature.
 */
export const UI_CONTENT_IDS = {
  /** Identity Widget — third-party client disclaimers & error messages (PROSERV-2561) */
  IDENTITY: 'ui_identity',
} as const;

/** Union type of all registered UI content IDs */
export type UIContentId = (typeof UI_CONTENT_IDS)[keyof typeof UI_CONTENT_IDS];

/** Variables shape for the GetUIContentById query */
export interface GetUIContentByIdVariables {
  getUiContentByIdId: string;
}

/**
 * Generic response shape for the GetUIContentById query.
 * The `getUIContentById` field returns a JSON object whose shape depends on the content ID.
 */
export interface GetUIContentByIdResponse<T = Record<string, unknown>> {
  getUIContentById: T | null;
}

/** Shape returned by the `useGetUIContent` hook. Mirrors Apollo's useQuery result. */
export interface UseGetUIContentResult<T = Record<string, unknown>> {
  data: GetUIContentByIdResponse<T> | undefined;
  loading: boolean;
  error: Error | undefined;
  /** Force a re-fetch, bypassing the cache. Returns the fresh payload. */
  refetch: () => Promise<GetUIContentByIdResponse<T> | undefined>;
}

/** Options accepted by the `useGetUIContent` hook. */
export interface UseGetUIContentOptions {
  /** Skip the fetch (matches Apollo's `skip` semantic). */
  skip?: boolean;
}

/* -------------------------------------------------------------------------- */
/* Internal: fetch                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Read the Content API endpoint from environment. Kept as a function (not a
 * module-level constant) so tests can mutate `process.env` between cases.
 */
const getContentApiUrl = (): string | undefined => process.env.REACT_APP_GQL_ENDPOINT;

/**
 * Resolve a Bearer access token for the Content API request.
 *
 * CMS content is fetched on pre-auth screens, so we always ensure a valid
 * transient (anonymous) token — clearing any stale/expired member token that
 * may linger in the token manager after logout before re-acquiring.
 */
const getAuthToken = async (): Promise<string | undefined> => {
  try {
    return await ensureTransientToken();
  } catch {
    return undefined;
  }
};

/**
 * Build the request headers, including the `Authorization: Bearer <token>`
 * header and the standard MGM channel/source/correlation headers used by
 * `web-mfe-account`'s Apollo `authMiddleware`.
 */
const buildContentApiHeaders = async (): Promise<Headers> => {
  const headers = new Headers();
  headers.set('x-mgm-channel', 'web');
  headers.set('x-mgm-source', 'mgmri');

  const token = await getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const journeyId = window.mgm?.logger?.getJourneyId?.();
  if (journeyId) headers.set('x-mgm-journey-id', journeyId);

  const correlationId = window.mgm?.logger?.getCorrelationId?.();
  if (correlationId) headers.set('x-mgm-correlation-id', correlationId);

  return headers;
};

export async function fetchUIContent<T = Record<string, unknown>>(
  contentId: string,
): Promise<GetUIContentByIdResponse<T>> {
  const url = getContentApiUrl();
  if (!url) {
    return { getUIContentById: null };
  }

  try {
    const headers = await buildContentApiHeaders();
    const { response } = await post(
      url,
      {
        headers,
        body: JSON.stringify({
          query: GET_UI_CONTENT_BY_ID,
          variables: { getUiContentByIdId: contentId },
        }),
      },
      undefined,
      false, // Handled correlation headers manually/custom
      true, // sendCredentials
      true, // externalEndpoint
    );

    const json = response as { data?: GetUIContentByIdResponse<unknown>; errors?: unknown };
    if (!json || json.errors || !json.data) {
      return { getUIContentById: null };
    }

    const rawVal = json.data.getUIContentById;
    let parsedVal = rawVal;
    if (typeof rawVal === 'string') {
      try {
        parsedVal = JSON.parse(rawVal);
      } catch (e) {
        // Safe logging - do not crash.
        captureException(e instanceof Error ? e : new Error(String(e)), 'ui-content', {
          message: 'Failed to parse getUIContentById response string into JSON',
        });
        return { getUIContentById: null };
      }
    }

    return {
      getUIContentById: parsedVal,
    } as GetUIContentByIdResponse<T>;
  } catch {
    // Never propagate — CMS outage must not break sign-in.
    return { getUIContentById: null };
  }
}

/* -------------------------------------------------------------------------- */
/* Public hook                                                                */
/* -------------------------------------------------------------------------- */

/**
 * React hook to fetch UI content from CMS by ID.
 *
 * @param contentId - The CMS content ID to fetch (e.g., `UI_CONTENT_IDS.IDENTITY`)
 * @param options   - Optional `{ skip, fetchPolicy }`
 */
export function useGetUIContent<T = Record<string, unknown>>(
  contentId: string,
  options: UseGetUIContentOptions = {},
): UseGetUIContentResult<T> {
  const { skip = false } = options;
  const [data, setData] = useState<GetUIContentByIdResponse<T> | undefined>(undefined);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const result = await fetchUIContent<T>(contentId);
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contentId, skip]);

  const refetch = useCallback(async () => {
    setLoading(true);
    const result = await fetchUIContent<T>(contentId);
    setData(result);
    setLoading(false);
    return result;
  }, [contentId]);

  return { data, loading, error, refetch };
}
